<template>
  <div class="tos-page">
    <!-- PAGE HERO -->
    <section class="page-hero">
      <div class="page-hero-inner">
        <h1>{{ heading }}</h1>
        <p>
          Understanding the rules, rights, and responsibilities that govern your
          use of our service.
        </p>
      </div>
    </section>

    <!-- MOBILE SECTION NAV (pill tabs) -->
    <nav
      class="mobile-section-nav"
      ref="mobileNav"
      aria-label="Terms sections (mobile)"
    >
      <a
        v-for="s in sections"
        :key="'m-' + s.id"
        :href="'#' + s.id"
        :class="{ active: activeId === s.id }"
        @click.prevent="scrollToSection(s.id)"
        >{{ s.short }}</a
      >
    </nav>

    <!-- MAIN LAYOUT -->
    <div class="main-layout">
      <!-- Sidebar -->
      <aside class="sidebar" aria-label="On this page">
        <p class="sidebar-label">On this page</p>
        <ul class="sidebar-nav">
          <li v-for="s in sections" :key="'s-' + s.id">
            <a
              :href="'#' + s.id"
              :class="{ active: activeId === s.id }"
              @click.prevent="scrollToSection(s.id)"
              >{{ s.long }}</a
            >
          </li>
        </ul>
      </aside>

      <!-- Policy content -->
      <div class="policy-content">
        <!-- What are ToS? -->
        <section class="policy-section reveal" id="what-are-tos">
          <p class="section-eyebrow">Overview</p>
          <h2 class="section-title">
            Understanding the Importance of Terms &amp; Conditions
          </h2>
          <p>
            In today's digital age, "Terms &amp; Conditions" (T&amp;Cs) have
            become an integral part of our online experience. Whether signing up
            for a new social media platform, downloading an app, or making an
            online purchase, we are often required to agree to these terms.
            Despite their ubiquity, many users pay little attention to what they
            entail. This article aims to shed light on what Terms &amp;
            Conditions are and why they matter.
          </p>
          <h2 class="section-title" style="margin-top: 32px">
            What Are Terms &amp; Conditions?
          </h2>
          <p>
            Terms &amp; Conditions, sometimes referred to as Terms of Service
            (T&amp;Cs) or End-User License Agreements (EULAs), are legal
            agreements between a service provider and its users. These documents
            outline the rules, regulations, and guidelines that users must
            adhere to when using a particular service. They also define the
            rights and responsibilities of both parties, helping to clarify what
            is permissible and what is not.
          </p>
        </section>

        <!-- Components -->
        <section class="policy-section reveal" id="components">
          <p class="section-eyebrow">Structure</p>
          <h2 class="section-title">Components of Terms &amp; Conditions</h2>
          <p>
            A typical ToS agreement covers various aspects including the
            following key areas.
          </p>

          <ul class="policy-list">
            <li class="policy-list-item" id="user-rights">
              <div class="list-num">1</div>
              <div class="list-body">
                <strong>User Rights and Obligations</strong>
                <p>
                  Specifies what users are allowed and not allowed to do. This
                  might include restrictions on reselling the service or
                  prohibiting illegal activities.
                </p>
              </div>
            </li>
            <li class="policy-list-item" id="intellectual-property">
              <div class="list-num">2</div>
              <div class="list-body">
                <strong>Intellectual Property</strong>
                <p>
                  Clarifies the ownership of content provided by both the
                  service provider and the user. It outlines what rights the
                  user has in sharing or modifying content.
                </p>
              </div>
            </li>
            <li class="policy-list-item" id="liability">
              <div class="list-num">3</div>
              <div class="list-body">
                <strong>Liability and Disclaimers</strong>
                <p>
                  Limits the service provider's liability in the event of
                  errors, outages, or damages incurred while using the service.
                </p>
              </div>
            </li>
            <li class="policy-list-item" id="privacy">
              <div class="list-num">4</div>
              <div class="list-body">
                <strong>Privacy Policy</strong>
                <p>
                  Explains how the provider collects, uses, and protects user
                  data. In the EU, this component must comply with the General
                  Data Protection Regulation (GDPR).
                </p>
              </div>
            </li>
            <li class="policy-list-item" id="termination">
              <div class="list-num">5</div>
              <div class="list-body">
                <strong>Termination Clauses</strong>
                <p>
                  States under what conditions either party can terminate the
                  agreement, detailing the process for account suspension or
                  closure.
                </p>
              </div>
            </li>
            <li class="policy-list-item" id="dispute">
              <div class="list-num">6</div>
              <div class="list-body">
                <strong>Dispute Resolution</strong>
                <p>
                  Offers guidelines on how disputes will be resolved, including
                  information about arbitration or governing law jurisdictions.
                </p>
              </div>
            </li>
          </ul>
        </section>

        <!-- Why important -->
        <section class="policy-section reveal" id="why-important">
          <p class="section-eyebrow">Significance</p>
          <h2 class="section-title">
            Why Are Terms &amp; Conditions Important?
          </h2>

          <ul class="policy-list">
            <li class="policy-list-item">
              <div class="list-num">1</div>
              <div class="list-body">
                <strong>Protecting Both Parties</strong>
                <p>
                  ToS agreements help protect the interests of both the service
                  provider and the user by setting clear expectations and
                  responsibilities. They act as a reference point in case of
                  disputes.
                </p>
              </div>
            </li>
            <li class="policy-list-item">
              <div class="list-num">2</div>
              <div class="list-body">
                <strong>Legal Enforceability</strong>
                <p>
                  Having a well-drafted ToS gives service providers a legal
                  backbone to enforce rules and regulations. It ensures that
                  users are aware of their legal standing and the implications
                  of their actions.
                </p>
              </div>
            </li>
            <li class="policy-list-item">
              <div class="list-num">3</div>
              <div class="list-body">
                <strong>Transparency and Trust</strong>
                <p>
                  Clear and concise terms help build trust between service
                  providers and users. When users understand their rights and
                  obligations, they are more likely to engage with the service
                  confidently.
                </p>
              </div>
            </li>
          </ul>
        </section>

        <!-- Challenges -->
        <section class="policy-section reveal" id="challenges">
          <p class="section-eyebrow">Considerations</p>
          <h2 class="section-title">Challenges and Criticisms</h2>
          <p>
            Despite their importance, ToS agreements often face criticism. The
            most common issue is the complexity and length of these documents,
            which can discourage users from reading them thoroughly. As a
            result, many users consent without understanding the implications
            fully.
          </p>
          <p>
            Recent movements advocate for simplified and summarised ToS
            agreements, allowing users to grasp essential information quickly.
            Additionally, with evolving legal and digital landscapes, there is a
            continuous need for service providers to update their terms to
            reflect changes in technology and law.
          </p>
        </section>

        <!-- Conclusion -->
        <section class="policy-section reveal" id="conclusion">
          <p class="section-eyebrow">Summary</p>
          <h2 class="section-title">Conclusion</h2>
          <p>
            Terms &amp; Conditions are crucial instruments in the digital world,
            helping to define and protect the relationship between service
            providers and users. While they can be dense and legally complex,
            their role in providing a secure and understandable framework cannot
            be understated. As users, being informed about these terms can lead
            to a more empowered and responsible online presence. Service
            providers, on the other hand, should strive for transparency and
            clarity, ensuring their terms are both protective and user-friendly.
          </p>
        </section>

        <!-- Contact CTA -->
        <div class="contact-cta reveal">
          <div class="contact-cta-text">
            <h3>Have questions about our terms?</h3>
            <p>Our team is happy to clarify anything in these terms for you.</p>
          </div>
          <a
            href="mailto:support@hooliganseyewear.com"
            class="btn-outline-white"
            >Contact us</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "./terms_of_services.css";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

export default {
  name: "TermsOfServices",
  setup() {
    return useStaticPageSeo({
      title: "Terms & Conditions | Hooligans Eyewear",
      heading: "Terms & Conditions",
      description:
        "Read the Hooligans Eyewear terms and conditions covering website use, orders, payments, shipping, returns, and customer responsibilities.",
    });
  },
  data() {
    return {
      activeId: "what-are-tos",
      sections: [
        { id: "what-are-tos", short: "What are ToS?", long: "What are ToS?" },
        { id: "components", short: "Components", long: "Components" },
        {
          id: "user-rights",
          short: "User rights",
          long: "User rights & obligations",
        },
        {
          id: "intellectual-property",
          short: "Intellectual property",
          long: "Intellectual property",
        },
        {
          id: "liability",
          short: "Liability",
          long: "Liability & disclaimers",
        },
        { id: "privacy", short: "Privacy policy", long: "Privacy policy" },
        {
          id: "termination",
          short: "Termination",
          long: "Termination clauses",
        },
        {
          id: "dispute",
          short: "Dispute resolution",
          long: "Dispute resolution",
        },
        {
          id: "why-important",
          short: "Why it matters",
          long: "Why it matters",
        },
        {
          id: "challenges",
          short: "Challenges",
          long: "Challenges & criticisms",
        },
      ],
    };
  },
  mounted() {
    if (typeof window === "undefined") return;

    this._reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._onScroll = () => this._updateActive();
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll, { passive: true });
    this.$nextTick(() => this._updateActive());
    this._setupRevealObserver();
  },
  beforeUnmount() {
    if (typeof window === "undefined") return;
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onScroll);
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    _setupRevealObserver() {
      const els = this.$el.querySelectorAll(".reveal");
      if (!els.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        els.forEach((el) => el.classList.add("visible"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              this._revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this._revealObserver.observe(el));
    },
    _getClickOffset() {
      if (typeof window === "undefined") return 24;
      return window.innerWidth <= 900 ? 80 : 24;
    },

    _updateActive() {
      const els = this.sections
        .map((s) => ({ id: s.id, el: document.getElementById(s.id) }))
        .filter((x) => x.el);
      if (!els.length) return;

      let current = els[0].id;
      for (const { id, el } of els) {
        if (el.getBoundingClientRect().top <= 120) current = id;
      }
      if (current !== this.activeId) {
        this.activeId = current;
        this.$nextTick(() => this._scrollActiveIntoView());
      }
    },

    _scrollActiveIntoView() {
      if (typeof document === "undefined") return;
      const root = this.$el;
      if (!root) return;
      const candidates = [
        root.querySelector(".sidebar-nav"),
        this.$refs.mobileNav,
      ].filter(Boolean);
      for (const nav of candidates) {
        if (!nav.offsetWidth) continue;
        if (nav.scrollWidth <= nav.clientWidth) continue;
        const active = nav.querySelector("a.active");
        if (!active) continue;
        const left = active.offsetLeft;
        const width = active.offsetWidth;
        nav.scrollLeft = left - nav.offsetWidth / 2 + width / 2;
      }
    },

    scrollToSection(id) {
      if (typeof window === "undefined") return;
      const target = document.getElementById(id);
      if (!target) return;
      const offset = this._getClickOffset();
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      this.activeId = id;
      this.$nextTick(() => this._scrollActiveIntoView());
    },
  },
};
</script>
